import React, { useState, useRef, useEffect } from "react";
import { BaseButton } from "src/components/ui/baseButton";
import { FileDownloadIcon } from "../icons";
import { Text } from "src/components/ui/Text";
import { useDempStore, useTHPStore } from "src/context/stores";
import { downloadYearlyReport } from "src/api/company";
import { useTranslation } from "react-i18next";

const BtnYearlyReport = () => {
  const [plantId] = useDempStore((state) => [state.plantId]);
  const { infoView } = useTHPStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const dropdownRef = useRef(null);
  const { t } = useTranslation("settings");

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const onClickDownload = async () => {
    try {
      setIsLoading(true);
      const reportType =
        infoView === "truck"
          ? "excel_vehicle_yearly"
          : "excel_charger_yearly";

      const { data, error } = await downloadYearlyReport(
        plantId,
        reportType,
        selectedYear
      );

      if (error) {
        console.error("API Error:", error);
        alert("Failed to download the report: " + (error.message || "Unknown error"));
        return;
      }

      if (data && data.download_url) {
        const link = document.createElement("a");
        link.href = data.download_url;
        link.download = `${infoView}_yearly_report_${selectedYear}.xlsx`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (data && typeof data === "string" && data.startsWith("http")) {
        const link = document.createElement("a");
        link.href = data;
        link.download = `${infoView}_yearly_report_${selectedYear}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Unexpected data format:", data);
        alert("Unexpected response format from server");
      }
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download the report, please try again...");
    } finally {
      setIsLoading(false);
      setShowDropdown(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <BaseButton
        variant="primary"
        size="normal"
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isLoading}
      >
        <div className="flex gap-1 items-center">
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <Text variant="semi-12">Downloading...</Text>
            </>
          ) : (
            <>
              <FileDownloadIcon />
              <Text variant="semi-12">{t("report.yearly_report")}</Text>
            </>
          )}
        </div>
      </BaseButton>

      {showDropdown && !isLoading && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-quartenary rounded-lg shadow-content-box p-3 min-w-[200px]">
          <div className="flex flex-col gap-2">
            <Text variant="medium-12" className="text-secondary">
              {t("report.select_year")}
            </Text>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-vekin"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <BaseButton
              variant="primary"
              size="normal"
              onClick={onClickDownload}
              className="w-full"
            >
              <div className="flex gap-1 items-center justify-center">
                <FileDownloadIcon />
                <Text variant="semi-12">{t("report.download_report")}</Text>
              </div>
            </BaseButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default BtnYearlyReport;
